import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { cn } from '../../lib/cn.js'
import { formatDate } from '../../lib/format.js'
import { useSession } from '../../lib/session.js'
import { useTRPC } from '../../lib/trpc.js'
import { Button, Field, Input, Select, Textarea, humanise } from '../ui.js'
import { Label, PanelHeading, RailFact } from './panes.js'
import type { Job } from './types.js'

type Site = NonNullable<Job['site']>

const ACCESS_METHODS = [
  'CLIENT_PRESENT',
  'KEY_SAFE',
  'GATE_CODE',
  'AGENT_KEY',
  'OPEN_ACCESS',
  'OTHER',
] as const

/**
 * What the property is like to turn up at.
 *
 * This is the half of the old job description that outlives the job. The
 * business was re-collecting it on every visit as checklist ticks — "Pets on
 * site" 7,375 times across 12,260 properties that get repeat work — because
 * ServiceM8 had nowhere to keep it. Decision 41.
 *
 * Three things about how it renders:
 *
 * **Unknown is shown, not hidden.** A pets field that is null draws as "Not
 * known" rather than disappearing, because a technician needs to be able to
 * tell "no dog" from "nobody asked" before opening a side gate. Hiding the
 * unanswered questions is what let seven years go by with none of them
 * answered.
 *
 * **Anyone with `JOB_EDIT` can correct it.** The person who finds the dog is
 * the technician, and technicians do not have `CLIENT_EDIT` — same split as
 * check-in in decision 19.
 *
 * **Hazards are amber and last.** Everything above is convenience; this is
 * the line that stops somebody walking into something.
 */
export function SiteKnowledgePane({ site }: { site: Site }) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { can } = useSession()

  const [editing, setEditing] = useState(false)

  const save = useMutation(
    trpc.clients.updateSiteKnowledge.mutationOptions({
      onSuccess: () => {
        setEditing(false)
        void queryClient.invalidateQueries(trpc.jobs.pathFilter())
        void queryClient.invalidateQueries(trpc.clients.pathFilter())
      },
    }),
  )

  if (editing) {
    return (
      <SiteKnowledgeForm
        site={site}
        isSaving={save.isPending}
        onCancel={() => setEditing(false)}
        onSave={(values) => save.mutate({ id: site.id, ...values })}
      />
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <PanelHeading
        title="Getting in"
        action={
          can('JOB_EDIT') ? (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-xs font-semibold text-brand-600 hover:text-brand-700"
            >
              {isBlank(site) ? 'Fill in' : 'Correct'}
            </button>
          ) : null
        }
      />

      <dl className="grid grid-cols-[auto_1fr] gap-x-5 gap-y-1.5 text-[12.5px]">
        <RailFact label="Pets">
          <Known value={site.hasPets ?? null} />
        </RailFact>
        <RailFact label="Children">
          <Known value={site.hasChildren ?? null} />
        </RailFact>
        {site.accessMethod ? <RailFact label="Access">{humanise(site.accessMethod)}</RailFact> : null}
        {site.gateCode ? (
          <RailFact label="Code">
            <span className="font-mono font-semibold">{site.gateCode}</span>
          </RailFact>
        ) : null}
        {site.parkingNotes ? (
          <RailFact label="Parking">
            <span className="whitespace-pre-wrap">{site.parkingNotes}</span>
          </RailFact>
        ) : null}
        <RailFact label="Notes">
          {site.accessNotes ? (
            <span className="whitespace-pre-wrap">{site.accessNotes}</span>
          ) : (
            <span className="text-ink-400">—</span>
          )}
        </RailFact>
      </dl>

      {site.hazardNotes ? (
        <p className="rounded-md bg-amber-500 px-2.5 py-2 text-[13px] leading-relaxed font-medium whitespace-pre-wrap text-ink-900">
          {site.hazardNotes}
        </p>
      ) : null}

      <p className="text-[11.5px] text-ink-400">{reviewLine(site)}</p>
    </div>
  )
}

/**
 * Yes / No / Not known, as three visibly different things.
 *
 * A boolean rendered as a checkbox collapses the third state into the second,
 * which is the specific mistake this field exists to avoid.
 */
function Known({ value }: { value: boolean | null }) {
  if (value === true) return <span className="font-semibold text-amber-800">Yes</span>
  if (value === false) return <span>No</span>
  return <span className="text-ink-400">Not known</span>
}

function isBlank(site: Site): boolean {
  return (
    site.hasPets == null &&
    site.hasChildren == null &&
    site.accessMethod == null &&
    !site.gateCode &&
    !site.accessNotes &&
    !site.parkingNotes &&
    !site.hazardNotes
  )
}

function reviewLine(site: Site): string {
  if (site.knowledgeReviewedAt) return `Checked ${formatDate(site.knowledgeReviewedAt)}`
  return isBlank(site) ? 'Nobody has filled this in' : 'Never confirmed'
}

/* -------------------------------------------------------------------------- */

interface Values {
  hasPets: boolean | null
  hasChildren: boolean | null
  accessMethod: (typeof ACCESS_METHODS)[number] | null
  gateCode: string | null
  accessNotes: string | null
  parkingNotes: string | null
  hazardNotes: string | null
}

function SiteKnowledgeForm({
  site,
  isSaving,
  onCancel,
  onSave,
}: {
  site: Site
  isSaving: boolean
  onCancel: () => void
  onSave: (values: Values) => void
}) {
  const [values, setValues] = useState<Values>({
    hasPets: site.hasPets ?? null,
    hasChildren: site.hasChildren ?? null,
    accessMethod: site.accessMethod ?? null,
    gateCode: site.gateCode ?? null,
    accessNotes: site.accessNotes ?? null,
    parkingNotes: site.parkingNotes ?? null,
    hazardNotes: site.hazardNotes ?? null,
  })

  const set = <K extends keyof Values>(key: K, value: Values[K]) =>
    setValues((current) => ({ ...current, [key]: value }))

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault()
        onSave(values)
      }}
    >
      <Label>Getting in</Label>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Pets on site">
          <Tristate value={values.hasPets} onChange={(value) => set('hasPets', value)} />
        </Field>
        <Field label="Young children on site">
          <Tristate value={values.hasChildren} onChange={(value) => set('hasChildren', value)} />
        </Field>

        <Field label="How we get in" htmlFor="site-access-method">
          <Select
            id="site-access-method"
            value={values.accessMethod ?? ''}
            onChange={(event) =>
              set('accessMethod', (event.target.value || null) as Values['accessMethod'])
            }
          >
            <option value="">Not known</option>
            {ACCESS_METHODS.map((method) => (
              <option key={method} value={method}>
                {humanise(method)}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Gate or lock box code" htmlFor="site-gate-code">
          <Input
            id="site-gate-code"
            value={values.gateCode ?? ''}
            onChange={(event) => set('gateCode', event.target.value || null)}
            placeholder="2018"
          />
        </Field>
      </div>

      <Field
        label="Access notes"
        hint="How to find the meter, which door, who to ask for."
        htmlFor="site-access-notes"
      >
        <Textarea
          id="site-access-notes"
          value={values.accessNotes ?? ''}
          onChange={(event) => set('accessNotes', event.target.value || null)}
        />
      </Field>

      <Field label="Parking" htmlFor="site-parking">
        <Input
          id="site-parking"
          value={values.parkingNotes ?? ''}
          onChange={(event) => set('parkingNotes', event.target.value || null)}
          placeholder="Rear lane, permit on the dash"
        />
      </Field>

      <Field
        label="Hazards"
        hint="Anything that could hurt somebody who did not know about it."
        htmlFor="site-hazards"
      >
        <Textarea
          id="site-hazards"
          value={values.hazardNotes ?? ''}
          onChange={(event) => set('hazardNotes', event.target.value || null)}
        />
      </Field>

      <div className="flex justify-end gap-1.5">
        <Button type="button" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" loading={isSaving}>
          Save
        </Button>
      </div>
    </form>
  )
}

function Tristate({
  value,
  onChange,
}: {
  value: boolean | null
  onChange: (value: boolean | null) => void
}) {
  const options: Array<{ label: string; value: boolean | null }> = [
    { label: 'Not known', value: null },
    { label: 'No', value: false },
    { label: 'Yes', value: true },
  ]

  return (
    <div className="flex gap-1">
      {options.map((option) => (
        <button
          key={option.label}
          type="button"
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
          className={cn(
            'min-h-[var(--control-sm)] flex-1 rounded-md px-2 text-xs font-medium transition-colors',
            value === option.value
              ? 'bg-brand-800 text-white'
              : 'bg-ink-100 text-ink-600 hover:bg-ink-200',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}
